import React, { useState } from 'react';
import { Plus, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Modal } from '../ui/modal';
import { useDivisions } from '../../hooks/use-divisions';
import { useCreateDivisionUser } from '../../hooks/use-division-users';
import { useDivisionRoles } from '../../hooks/use-division-roles';
import { CreateUserRoleRequest } from '../../types';

interface DivisionRole {
  divisionId: string;
  divisionName: string;
  role: string;
}

interface AddUserToDivisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  userName: string;
  currentDivisionIds: string[];
  currentDivisions?: DivisionRole[];
}

export function AddUserToDivisionModal({
  isOpen,
  onClose,
  userEmail,
  userName,
  currentDivisionIds,
  currentDivisions = [],
}: AddUserToDivisionModalProps) {
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all divisions
  const { data: divisionsResponse } = useDivisions({ active: true });
  const divisions = divisionsResponse?.divisions || [];

  // Fetch roles for selected division
  const { data: rolesResponse } = useDivisionRoles(selectedDivisionId, !!selectedDivisionId);
  const divisionRoles = rolesResponse?.roles || [];

  // Get available divisions (excluding ones user is already in)
  const availableDivisions = divisions.filter(
    (div) => !currentDivisionIds.includes(div.id)
  );

  const createUserMutation = useCreateDivisionUser(selectedDivisionId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedDivisionId || !selectedRole) {
      setError('Please select both a division and a role');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create user in the new division
      await createUserMutation.mutateAsync({
        email: userEmail,
        firstName: userName.split(' ')[0] || '',
        lastName: userName.split(' ').slice(1).join(' ') || '',
        divisionRoles: [
          {
            divisionId: selectedDivisionId,
            role: selectedRole,
          },
        ],
      });

      handleClose();
    } catch (err: any) {
      setError(err?.apiError?.message || err?.message || 'Failed to add user to division');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedDivisionId('');
    setSelectedRole('');
    setError(null);
    onClose();
  };

  const defaultRoles = [
    { name: 'Shopper' },
    { name: 'Approver' },
    { name: 'DivisionAdmin' },
  ];

  const availableRoles = divisionRoles.length > 0 ? divisionRoles : defaultRoles;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add User to Another Division"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-950 p-4">
            <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
          </div>
        )}

        {/* User Info */}
        <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-4 border border-secondary-200 dark:border-secondary-700">
          <p className="text-sm font-medium text-secondary-900 dark:text-white">{userName}</p>
          <p className="text-sm text-secondary-600 dark:text-secondary-400">{userEmail}</p>
        </div>

        {/* Current Divisions */}
        {currentDivisions.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
              Current Divisions
            </label>
            <div className="space-y-2">
              {currentDivisions.map((divRole) => (
                <div
                  key={divRole.divisionId}
                  className="flex items-center justify-between p-3 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700"
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-secondary-500 dark:text-secondary-400" />
                    <span className="text-sm font-medium text-secondary-900 dark:text-white">
                      {divRole.divisionName}
                    </span>
                  </div>
                  <Badge variant="info">{divRole.role}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {availableDivisions.length === 0 ? (
          <div className="text-center py-8">
            <Building2 className="mx-auto h-12 w-12 text-secondary-400" />
            <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
              This user is already associated with all available divisions.
            </p>
          </div>
        ) : (
          <>
            {/* Division Selection */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                Select Division
              </label>
              <select
                value={selectedDivisionId}
                onChange={(e) => {
                  setSelectedDivisionId(e.target.value);
                  setSelectedRole(''); // Reset role when division changes
                }}
                className="w-full rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                required
              >
                <option value="">Select a division...</option>
                {availableDivisions.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.name} {division.code ? `(${division.code})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Role Selection */}
            {selectedDivisionId && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                  Select Role
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  required
                >
                  <option value="">Select a role...</option>
                  {availableRoles.map((role) => (
                    <option key={role.name} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!selectedDivisionId || !selectedRole || isSubmitting}
              >
                {isSubmitting ? 'Adding...' : 'Add to Division'}
              </Button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}
